// District Court scraper API helpers. Centralizes the base URL and gives every
// caller a single switch to enable payload encryption against the gateway.
//
// Envelope format (matches District-court-scraper/Encryption.md):
//   POST: body = { key, iv, data } (base64)
//   GET:  query string = ?key=...&iv=...&data=...
//   Encrypted responses come back as { iv, data } with X-Payload-Encrypted: 1.
//
// Helpers return a Response-like { ok, status, body } so call sites can keep
// their existing `if (!res.ok) throw ...` checks with a one-line rename.

import {
  getPublicKey,
  encryptRequest,
  decryptResponse,
  isEncryptedResponse,
} from "../utils/dcCrypto";

export const DC_API_BASE_URL =
  "https://district-court-scraper-596718606544.asia-south1.run.app";

// Backend at DC_API_BASE_URL must serve `GET /api/public-key` (returns RSA PEM)
// and the payload-encryption middleware must be live. Mirror to `false` if you
// ever roll back to a build without the middleware.
const ENCRYPTION_ENABLED = true;

const JSON_HEADERS = { "Content-Type": "application/json" };

function headersToObject(headers) {
  const out = {};
  if (!headers || typeof headers.forEach !== "function") return out;
  headers.forEach((value, key) => {
    out[key.toLowerCase()] = value;
  });
  return out;
}

async function parseJsonSafe(response) {
  try {
    return await response.json();
  } catch (_) {
    return null;
  }
}

async function unwrapBody(response, aesKey) {
  const headers = headersToObject(response.headers);
  const body = await parseJsonSafe(response);
  if (aesKey && body && isEncryptedResponse(headers, body)) {
    return await decryptResponse(body, aesKey);
  }
  return body;
}

export async function dcPost(path, data) {
  const url = `${DC_API_BASE_URL}${path}`;

  if (!ENCRYPTION_ENABLED) {
    const response = await fetch(url, {
      method: "POST",
      headers: JSON_HEADERS,
      body: JSON.stringify(data ?? {}),
    });
    const body = await parseJsonSafe(response);
    return { ok: response.ok, status: response.status, body };
  }

  const publicKey = await getPublicKey(DC_API_BASE_URL);
  const { envelope, aesKey } = await encryptRequest(data, publicKey);
  const response = await fetch(url, {
    method: "POST",
    headers: JSON_HEADERS,
    body: JSON.stringify(envelope),
  });
  const body = await unwrapBody(response, aesKey);
  return { ok: response.ok, status: response.status, body };
}

// Fetch a binary response (e.g. a court-order PDF) that is gated behind the
// encrypted query envelope. On success the gateway streams raw bytes (PDF); on
// error it returns JSON, which may itself be an encrypted { iv, data } envelope.
// Returns { ok, status, blob, contentType } on success, or { ok:false, error }.
export async function dcGetBlob(path, params) {
  const url = `${DC_API_BASE_URL}${path}`;

  let fetchUrl = url;
  let aesKey = null;
  if (ENCRYPTION_ENABLED) {
    const publicKey = await getPublicKey(DC_API_BASE_URL);
    const enc = await encryptRequest(params ?? {}, publicKey);
    aesKey = enc.aesKey;
    const search = new URLSearchParams();
    search.set("key", enc.envelope.key);
    search.set("iv", enc.envelope.iv);
    search.set("data", enc.envelope.data);
    fetchUrl = `${url}?${search.toString()}`;
  } else {
    const search = new URLSearchParams();
    if (params && typeof params === "object") {
      for (const [k, v] of Object.entries(params)) {
        if (v !== undefined && v !== null) search.set(k, String(v));
      }
    }
    const qs = search.toString();
    fetchUrl = qs ? `${url}?${qs}` : url;
  }

  const response = await fetch(fetchUrl);
  const headers = headersToObject(response.headers);
  const contentType = headers["content-type"] || "";

  // Error path: gateway returned JSON (possibly encrypted) rather than a PDF.
  if (!response.ok || contentType.includes("application/json")) {
    let body = await parseJsonSafe(response);
    if (aesKey && body && isEncryptedResponse(headers, body)) {
      try {
        body = await decryptResponse(body, aesKey);
      } catch (_) {
        /* leave body as-is */
      }
    }
    return {
      ok: false,
      status: response.status,
      error: body?.error || `HTTP ${response.status}`,
    };
  }

  return {
    ok: true,
    status: response.status,
    blob: await response.blob(),
    contentType,
  };
}

export async function dcGet(path, params) {
  const search = new URLSearchParams();

  if (ENCRYPTION_ENABLED) {
    const publicKey = await getPublicKey(DC_API_BASE_URL);
    const { envelope, aesKey } = await encryptRequest(params ?? {}, publicKey);
    search.set("key", envelope.key);
    search.set("iv", envelope.iv);
    search.set("data", envelope.data);
    const response = await fetch(`${DC_API_BASE_URL}${path}?${search.toString()}`);
    const body = await unwrapBody(response, aesKey);
    return { ok: response.ok, status: response.status, body };
  }

  if (params && typeof params === "object") {
    for (const [k, v] of Object.entries(params)) {
      if (v !== undefined && v !== null) search.set(k, String(v));
    }
  }
  const qs = search.toString();
  const url = qs ? `${DC_API_BASE_URL}${path}?${qs}` : `${DC_API_BASE_URL}${path}`;
  const response = await fetch(url);
  const body = await parseJsonSafe(response);
  return { ok: response.ok, status: response.status, body };
}
